import {
	DEFAULT_TIMEOUT,
	loadBridgeBail,
	loadBridgeRace,
	loadBridgeStrict,
	MAX_RETRIES,
	SHOULD_LOG_ERROR,
} from './load-bridge-fns/index.tsx';

export type LoadBridgeOptions = {
	/**
	 * AMD name of the resource being loaded
	 * (eg: what does the module include in its `define('some/name', () => ...)`,
	 */
	name: string;
	/**
	 * WRM key (or keys) of the module to load if 'name' is missing.
	 * @example
	 * `wrc!com.atlassian.plugin:some-resource-key`
	 */
	wrmKeys?: string | string[];
	/**
	 * Attempts
	 */
	tentatives?: number;
	timeout?: number;
	shouldLogError?: boolean;
};

/**
 * The original loadBridge is deprecated, please use the new precise methods.
 * @deprecated Use one of the new precise methods: loadBridgeStrict, loadBridgeRace, loadBridgeBail, loadBridgeWeak
 */
export const loadBridge = <T,>({
	name,
	wrmKeys,
	tentatives = MAX_RETRIES,
	timeout = DEFAULT_TIMEOUT,
	shouldLogError = SHOULD_LOG_ERROR,
}: LoadBridgeOptions): Promise<T> => {
	if (wrmKeys != null) {
		return loadBridgeStrict<T>({
			name,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			wrmKeys: ([] as string[]).concat(wrmKeys),
			shouldLogError,
		});
	}
	if (tentatives <= 0) {
		return Promise.reject(new Error('Unable to load bridge, zero tentatives never works'));
	}
	if (tentatives <= 1) {
		return loadBridgeBail<T>({ name, shouldLogError });
	}
	return loadBridgeRace<T>({ name, shouldLogError, timeout: (tentatives - 1) * timeout });
};

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export {
	loadBridgeBail,
	type LoadBridgeBailOptions,
	loadBridgeRace,
	type LoadBridgeRaceOptions,
	loadBridgeStrict,
	type LoadBridgeStrictOptions,
	loadBridgeWeak,
	type LoadBridgeWeakOptions,
	loadWrmResources,
	type LoadWrmResourcesArg,
	waitForHeritage,
	wrmRequire,
	wrmRequireWithPrefetch,
	type Options,
	type BatchDetailsResponseType,
} from './load-bridge-fns';
